import React, { useCallback, useEffect, useRef } from 'react';
import { Typography, Box, Button, Chip } from '@mui/material';
import { useValue } from '@tldraw/state-react';
import { useEditor, stopEventPropagation } from 'tldraw';
import { T } from '@tldraw/validate';
import { BaseBoxShapeUtil, HTMLContainer } from 'tldraw';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import classNames from 'classnames';
import LazyImage from 'components/LazyImage';
import { getCardImageHoverScale } from 'constants/cardImageZoom';
import { useBoardAiContext } from '../context/BoardAiContext';
import { extractBoardDocumentSnapshot } from '../utils/boardSnapshot';
import { usePinCardInViewport } from '../hooks/usePinCardInViewport';
import { isCanvasStrokeInProgress } from '../utils/canvasToolState';
import { startBoardPinPointerInteraction } from '../utils/pinCardDrag';
import {
  SUGGEST_PIN_WIDTH,
  SUGGEST_PIN_MIN_HEIGHT,
} from '../constants/suggestPinLayout';
import { CONVERSATION_PIN_TYPE } from '../utils/createConversationPin';
import { openPinContextMenu } from '../utils/openTldrawContextMenu';
import {
  boardInvZoomPx,
  boardPinHeaderHeightPx,
  boardPinHeaderScreenPx,
  readBoardInvZoom,
} from '../utils/boardCameraCss';

const BOARD_HEADER_SCALE = 0.8;

const conversationPinShapeProps = {
  w: T.nonZeroNumber,
  h: T.nonZeroNumber,
  title: T.string,
  snippet: T.string,
  imageUrl: T.string,
  sourceUrl: T.string,
  conversationId: T.string,
  pinKind: T.string,
  messageAt: T.string,
};

function useAutoConversationPinHeight(editor, shape) {
  const contentRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;

    const syncHeight = () => {
      if (isCanvasStrokeInProgress(editor)) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const currentShape = editor.getShape(shape.id);
        if (!currentShape) return;

        const measured = Math.ceil(el.scrollHeight);
        if (measured < SUGGEST_PIN_MIN_HEIGHT) return;

        const nextHeight = Math.max(measured, SUGGEST_PIN_MIN_HEIGHT);
        if (Math.abs(nextHeight - currentShape.props.h) <= 2) return;

        editor.updateShape({
          id: shape.id,
          type: CONVERSATION_PIN_TYPE,
          props: { h: nextHeight },
        });
      });
    };

    const resizeObserver = new ResizeObserver(syncHeight);
    resizeObserver.observe(el);
    syncHeight();

    return () => {
      resizeObserver.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    editor,
    shape.id,
    shape.props.w,
    shape.props.title,
    shape.props.snippet,
    shape.props.imageUrl,
    shape.props.pinKind,
  ]);

  return contentRef;
}

function useAutoConversationPinWidth(editor, shape) {
  useEffect(() => {
    if (isCanvasStrokeInProgress(editor)) return undefined;

    const currentShape = editor.getShape(shape.id);
    if (!currentShape || Math.abs(SUGGEST_PIN_WIDTH - currentShape.props.w) <= 2) return undefined;

    editor.updateShape({
      id: shape.id,
      type: CONVERSATION_PIN_TYPE,
      props: { w: SUGGEST_PIN_WIDTH },
    });
  }, [editor, shape.id, shape.props.w]);
}

function ConversationPinComponent({ shape }) {
  const editor = useEditor();
  const { onSaveSnapshot, openConversation } = useBoardAiContext();
  const {
    title,
    snippet,
    imageUrl,
    conversationId,
    pinKind,
    messageAt,
  } = shape.props;
  const lastTransformOriginRef = useRef('center');

  const boardSpacePx = px => `${px * BOARD_HEADER_SCALE}px`;

  const allowPinInteraction = useValue(
    'conversation-pin-pointer-events',
    () => editor.isIn('select'),
    [editor]
  );
  const loadImageWhen = usePinCardInViewport(editor, shape);
  const contentRef = useAutoConversationPinHeight(editor, shape);
  useAutoConversationPinWidth(editor, shape);

  const kindLabel = pinKind === 'exchange' ? 'Q&A' : 'Chat';

  const handlePinContextMenuCapture = useCallback(
    event => {
      openPinContextMenu(editor, shape.id, event);
    },
    [editor, shape.id]
  );

  const handleOpenConversation = useCallback(
    e => {
      stopEventPropagation(e);
      if (!conversationId) return;
      if (!editor.getInstanceState().isReadonly) {
        editor.markHistoryStoppingPoint('selecting conversation pin');
        editor.select(shape.id);
      }
      openConversation?.(conversationId, {
        messageAt: messageAt || undefined,
        readOnly: editor.getInstanceState().isReadonly,
      });
    },
    [conversationId, messageAt, openConversation, editor, shape.id]
  );

  const saveConversationPinSnapshot = useCallback(() => {
    try {
      onSaveSnapshot?.(extractBoardDocumentSnapshot(editor));
    } catch {
      /* ignore */
    }
  }, [editor, onSaveSnapshot]);

  const handleHeaderPointerDown = useCallback(
    event => {
      if (!allowPinInteraction) return;
      startBoardPinPointerInteraction(editor, event, {
        shapeId: shape.id,
        shapeType: CONVERSATION_PIN_TYPE,
        historyLabel: 'conversation pin',
        allowDrag: true,
        onDrop: saveConversationPinSnapshot,
      });
    },
    [editor, shape.id, allowPinInteraction, saveConversationPinSnapshot]
  );

  const handleBodyPointerDown = useCallback(
    event => {
      if (!allowPinInteraction) return;
      startBoardPinPointerInteraction(editor, event, {
        shapeId: shape.id,
        shapeType: CONVERSATION_PIN_TYPE,
        historyLabel: 'conversation pin',
        allowDrag: false,
      });
    },
    [editor, shape.id, allowPinInteraction]
  );

  return (
    <HTMLContainer
      id={shape.id}
      className="board-conversation-pin-container"
      style={{
        width: shape.props.w,
        height: shape.props.h,
        pointerEvents: 'none',
        overflow: 'visible',
      }}
      onContextMenuCapture={handlePinContextMenuCapture}
    >
      <div ref={contentRef} className="board-conversation-pin-measure">
        <Box
          className={classNames('board-conversation-pin', {
            'board-conversation-pin--pass-through': !allowPinInteraction,
          })}
          sx={{
            padding: boardSpacePx(10),
            gap: boardSpacePx(8),
            borderRadius: boardSpacePx(14),
          }}
        >
          <Box
            className="board-conversation-pin-drag-handle"
            onPointerDown={allowPinInteraction ? handleHeaderPointerDown : undefined}
            sx={{
              boxSizing: 'border-box',
              height: boardPinHeaderHeightPx(),
              minHeight: boardPinHeaderHeightPx(),
              maxHeight: boardPinHeaderHeightPx(),
              gap: boardInvZoomPx(8),
              paddingBottom: 0,
              overflow: 'hidden',
              alignItems: 'center',
              cursor: allowPinInteraction ? 'grab' : undefined,
              '&:active': allowPinInteraction ? { cursor: 'grabbing' } : undefined,
            }}
          >
            <Chip
              size="small"
              icon={<SmartToyOutlinedIcon sx={{ fontSize: boardPinHeaderScreenPx() }} />}
              label={kindLabel}
              className="board-conversation-pin-kind"
              sx={{
                height: boardInvZoomPx(22),
                maxHeight: boardInvZoomPx(22),
                fontSize: boardPinHeaderScreenPx(),
                minWidth: 0,
                maxWidth: '100%',
                '& .MuiChip-label': {
                  px: boardInvZoomPx(6),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiChip-icon': { ml: boardInvZoomPx(4), flexShrink: 0 },
              }}
            />
          </Box>

          <Box
            className="board-conversation-pin-body"
            onPointerDown={allowPinInteraction ? handleBodyPointerDown : undefined}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: boardSpacePx(8),
              minWidth: 0,
              cursor: allowPinInteraction ? 'pointer' : undefined,
            }}
          >
            {imageUrl ? (
              <Box
                className="board-conversation-pin-image-area"
                sx={{ borderRadius: boardSpacePx(10) }}
              >
                <LazyImage
                  src={imageUrl}
                  when={loadImageWhen}
                  lazy={false}
                  wrapperClassName="board-conversation-pin-image-wrap"
                  wrapperStyle={{ width: '100%', height: '100%' }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                    transformOrigin: 'center',
                  }}
                  onMouseEnter={e => {
                    e.target.style.transition = 'transform 0.5s ease-in';
                    e.target.style.transform = `scale(${getCardImageHoverScale('BOARD', readBoardInvZoom(e.target))})`;
                  }}
                  onMouseMove={e => {
                    const rect = e.target.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    const origin = `${x}% ${y}%`;
                    e.target.style.transformOrigin = origin;
                    lastTransformOriginRef.current = origin;
                  }}
                  onMouseLeave={e => {
                    e.target.style.transformOrigin = lastTransformOriginRef.current;
                    e.target.style.transition = 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)';
                    e.target.style.transform = 'scale(1)';
                  }}
                />
              </Box>
            ) : (
              <Box
                className="board-conversation-pin-image-area board-conversation-pin-image-area--empty"
                sx={{ borderRadius: boardSpacePx(10) }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: boardSpacePx(28), color: '#94a3b8' }} />
              </Box>
            )}

            <Typography
              className="board-conversation-pin-title"
              noWrap
              sx={{
                fontSize: boardPinHeaderScreenPx(),
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title || 'Board AI chat'}
            </Typography>

            {snippet ? (
              <Typography
                className="board-conversation-pin-snippet"
                sx={{ fontSize: '7px', lineHeight: 1.35 }}
              >
                {snippet}
              </Typography>
            ) : null}
          </Box>

          <Box className="board-conversation-pin-actions" sx={{ gap: boardSpacePx(6), marginTop: boardSpacePx(2) }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: boardSpacePx(16) }} />}
              onPointerDown={stopEventPropagation}
              onClick={conversationId ? handleOpenConversation : undefined}
              sx={{
                textTransform: 'none',
                fontSize: boardSpacePx(12),
                lineHeight: 1.25,
                minHeight: boardSpacePx(30),
                px: boardSpacePx(10),
                py: boardSpacePx(4),
                borderRadius: boardSpacePx(8),
                '& .MuiButton-startIcon': {
                  marginLeft: boardSpacePx(-2),
                  marginRight: boardSpacePx(6),
                  '& > *:nth-of-type(1)': { fontSize: boardSpacePx(16) },
                },
              }}
            >
              Open chat
            </Button>
          </Box>
        </Box>
      </div>
    </HTMLContainer>
  );
}

export class ConversationPinShapeUtil extends BaseBoxShapeUtil {
  static type = CONVERSATION_PIN_TYPE;

  static props = conversationPinShapeProps;

  getDefaultProps() {
    return {
      w: SUGGEST_PIN_WIDTH,
      h: SUGGEST_PIN_MIN_HEIGHT,
      title: 'Board AI chat',
      snippet: '',
      imageUrl: '',
      sourceUrl: '',
      conversationId: '',
      pinKind: 'conversation',
      messageAt: '',
    };
  }

  canEdit = () => false;

  canResize = () => false;

  isAspectRatioLocked = () => false;

  component(shape) {
    return <ConversationPinComponent shape={shape} />;
  }

  indicator(shape) {
    return (
      <rect
        width={shape.props.w}
        height={shape.props.h}
        rx={14}
      />
    );
  }
}
